import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Lock, LogOut, Image, Calendar, Settings, Users, Mail, 
  Plus, Trash2, Edit, Check, X, Upload, Video, Eye, EyeOff,
  DollarSign, Phone, Instagram, ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [activeTab, setActiveTab] = useState("gallery");
  const [loading, setLoading] = useState(false);

  // Data states
  const [galleryItems, setGalleryItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [settings, setSettings] = useState(null);

  // Form states
  const [newGalleryItem, setNewGalleryItem] = useState({
    type: "image",
    src: "",
    alt: "",
    label: "",
    color: "rose",
    span: "col-span-1 row-span-1"
  });
  const [newEvent, setNewEvent] = useState({
    title: "",
    date: "",
    time: "",
    description: "",
    flyer_image: "",
    is_active: true
  });

  useEffect(() => {
    // Check for saved token
    const savedToken = localStorage.getItem("admin_token");
    if (savedToken) {
      setToken(savedToken);
      verifyToken(savedToken);
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Basic ${tokenToVerify}` }
      });
      setIsLoggedIn(true);
      setToken(tokenToVerify);
      loadData(tokenToVerify);
    } catch {
      localStorage.removeItem("admin_token");
      setIsLoggedIn(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      setToken(response.data.token);
      localStorage.setItem("admin_token", response.data.token);
      setIsLoggedIn(true);
      loadData(response.data.token);
      toast.success("Welcome back, Admin!");
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setIsLoggedIn(false);
    setToken("");
    toast.success("Logged out successfully");
  };

  const loadData = async (authToken) => {
    const headers = { Authorization: `Basic ${authToken}` };
    try {
      const [galleryRes, eventsRes, bookingsRes, inquiriesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/gallery`, { headers }),
        axios.get(`${API}/admin/events`, { headers }),
        axios.get(`${API}/admin/bookings`, { headers }),
        axios.get(`${API}/admin/inquiries`, { headers }),
        axios.get(`${API}/admin/settings`, { headers })
      ]);
      setGalleryItems(galleryRes.data);
      setEvents(eventsRes.data);
      setBookings(bookingsRes.data);
      setInquiries(inquiriesRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const headers = { Authorization: `Basic ${token}` };

  // Gallery functions
  const addGalleryItem = async () => {
    if (!newGalleryItem.src || !newGalleryItem.alt) {
      toast.error("Please fill in URL and description");
      return;
    }
    try {
      await axios.post(`${API}/admin/gallery`, newGalleryItem, { headers });
      toast.success("Gallery item added!");
      loadData(token);
      setNewGalleryItem({ type: "image", src: "", alt: "", label: "", color: "rose", span: "col-span-1 row-span-1" });
    } catch {
      toast.error("Failed to add item");
    }
  };

  const deleteGalleryItem = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, { headers });
      toast.success("Item deleted");
      loadData(token);
    } catch {
      toast.error("Failed to delete");
    }
  };

  // Event functions
  const addEvent = async () => {
    if (!newEvent.title || !newEvent.date) {
      toast.error("Please fill in title and date");
      return;
    }
    try {
      await axios.post(`${API}/admin/events`, newEvent, { headers });
      toast.success("Event added!");
      loadData(token);
      setNewEvent({ title: "", date: "", time: "", description: "", flyer_image: "", is_active: true });
    } catch {
      toast.error("Failed to add event");
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API}/admin/events/${id}`, { headers });
      toast.success("Event deleted");
      loadData(token);
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleEventActive = async (id, currentStatus) => {
    try {
      await axios.put(`${API}/admin/events/${id}`, { is_active: !currentStatus }, { headers });
      loadData(token);
    } catch {
      toast.error("Failed to update");
    }
  };

  // Booking functions
  const updateBookingStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${id}/status?status=${status}`, {}, { headers });
      toast.success("Status updated");
      loadData(token);
    } catch {
      toast.error("Failed to update");
    }
  };

  // Inquiry functions
  const updateInquiryStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/inquiries/${id}/status?status=${status}`, {}, { headers });
      toast.success("Status updated");
      loadData(token);
    } catch {
      toast.error("Failed to update");
    }
  };

  // Settings functions
  const updateSettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, settings, { headers });
      toast.success("Settings saved!");
    } catch {
      toast.error("Failed to save");
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="glass-card p-8">
            <div className="text-center mb-8">
              <Lock className="w-12 h-12 text-rose mx-auto mb-4" />
              <h1 className="font-heading text-3xl text-white">Admin Login</h1>
              <p className="text-zinc-400 mt-2">Daina Lanee Brand Dashboard</p>
            </div>
            
            <form onSubmit={handleLogin}>
              <div className="mb-6">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="w-full bg-zinc-900 border border-zinc-700 text-white p-4 focus:border-rose focus:outline-none"
                  data-testid="admin-password-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-gold w-full"
                data-testid="admin-login-btn"
              >
                {loading ? "Logging in..." : "Login"}
              </button>
            </form>
            
            <p className="text-center text-zinc-500 text-sm mt-6">
              <a href="/" className="text-rose hover:text-rose-light">← Back to website</a>
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-zinc-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Lock className="w-6 h-6 text-rose" />
            <h1 className="font-heading text-xl text-white">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-zinc-400 hover:text-white text-sm">View Site</a>
            <button onClick={handleLogout} className="flex items-center gap-2 text-zinc-400 hover:text-rose">
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { id: "gallery", icon: Image, label: "Gallery" },
            { id: "events", icon: Calendar, label: "Events" },
            { id: "bookings", icon: Users, label: "Bookings" },
            { id: "inquiries", icon: Mail, label: "Inquiries" },
            { id: "settings", icon: Settings, label: "Settings" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 transition-colors ${
                activeTab === tab.id
                  ? "bg-rose text-black"
                  : "bg-zinc-900 text-zinc-400 hover:text-white"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === "bookings" && bookings.filter(b => b.status === "pending").length > 0 && (
                <span className="bg-gold text-black text-xs px-2 py-0.5 rounded-full">
                  {bookings.filter(b => b.status === "pending").length}
                </span>
              )}
              {tab.id === "inquiries" && inquiries.filter(i => i.status === "new").length > 0 && (
                <span className="bg-gold text-black text-xs px-2 py-0.5 rounded-full">
                  {inquiries.filter(i => i.status === "new").length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Gallery Tab */}
        {activeTab === "gallery" && (
          <div>
            <h2 className="font-heading text-2xl text-white mb-6">Manage Gallery</h2>
            
            {/* Add New Item */}
            <div className="glass-card p-6 mb-8">
              <h3 className="font-medium text-white mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-rose" />
                Add New Item
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <select
                  value={newGalleryItem.type}
                  onChange={(e) => setNewGalleryItem({...newGalleryItem, type: e.target.value})}
                  className="bg-zinc-900 border border-zinc-700 text-white p-3"
                >
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
                <input
                  type="text"
                  placeholder="Image/Video URL"
                  value={newGalleryItem.src}
                  onChange={(e) => setNewGalleryItem({...newGalleryItem, src: e.target.value})}
                  className="bg-zinc-900 border border-zinc-700 text-white p-3"
                />
                <input
                  type="text"
                  placeholder="Description (alt text)"
                  value={newGalleryItem.alt}
                  onChange={(e) => setNewGalleryItem({...newGalleryItem, alt: e.target.value})}
                  className="bg-zinc-900 border border-zinc-700 text-white p-3"
                />
                {newGalleryItem.type === "video" && (
                  <>
                    <input
                      type="text"
                      placeholder="Video Label"
                      value={newGalleryItem.label}
                      onChange={(e) => setNewGalleryItem({...newGalleryItem, label: e.target.value})}
                      className="bg-zinc-900 border border-zinc-700 text-white p-3"
                    />
                    <select
                      value={newGalleryItem.color}
                      onChange={(e) => setNewGalleryItem({...newGalleryItem, color: e.target.value})}
                      className="bg-zinc-900 border border-zinc-700 text-white p-3"
                    >
                      <option value="rose">Rose</option>
                      <option value="gold">Gold</option>
                    </select>
                  </>
                )}
                <select
                  value={newGalleryItem.span}
                  onChange={(e) => setNewGalleryItem({...newGalleryItem, span: e.target.value})}
                  className="bg-zinc-900 border border-zinc-700 text-white p-3"
                >
                  <option value="col-span-1 row-span-1">Normal</option>
                  <option value="col-span-1 row-span-2">Tall</option>
                  <option value="col-span-1 md:col-span-2 row-span-1">Wide</option>
                </select>
              </div>
              <button onClick={addGalleryItem} className="btn-primary mt-4">
                Add to Gallery
              </button>
            </div>

            {/* Gallery Items */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {galleryItems.map((item) => (
                <div key={item.id} className="glass-card p-3 relative group">
                  {item.type === "video" ? (
                    <div className="aspect-square bg-zinc-800 flex items-center justify-center">
                      <Video className="w-8 h-8 text-rose" />
                    </div>
                  ) : (
                    <img src={item.src} alt={item.alt} className="aspect-square object-cover" />
                  )}
                  <p className="text-xs text-zinc-400 mt-2 truncate">{item.alt}</p>
                  <button
                    onClick={() => deleteGalleryItem(item.id)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            
            {galleryItems.length === 0 && (
              <p className="text-zinc-500 text-center py-12">No gallery items yet. Add some above!</p>
            )}
          </div>
        )}

        {/* Events Tab */}
        {activeTab === "events" && (
          <div>
            <h2 className="font-heading text-2xl text-white mb-6">Manage Events</h2>
            
            {/* Add New Event */}
            <div className="glass-card p-6 mb-8">
              <h3 className="font-medium text-white mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-rose" />
                Add New Event
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Event Title"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                  className="bg-zinc-900 border border-zinc-700 text-white p-3"
                />
                <input
                  type="text"
                  placeholder="Date (e.g., April 3rd, 2026)"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                  className="bg-zinc-900 border border-zinc-700 text-white p-3"
                />
                <input
                  type="text"
                  placeholder="Time (e.g., 8:00 PM - 10:30 PM)"
                  value={newEvent.time}
                  onChange={(e) => setNewEvent({...newEvent, time: e.target.value})}
                  className="bg-zinc-900 border border-zinc-700 text-white p-3"
                />
                <input
                  type="text"
                  placeholder="Flyer Image URL (optional)"
                  value={newEvent.flyer_image}
                  onChange={(e) => setNewEvent({...newEvent, flyer_image: e.target.value})}
                  className="bg-zinc-900 border border-zinc-700 text-white p-3"
                />
                <textarea
                  placeholder="Description"
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                  className="bg-zinc-900 border border-zinc-700 text-white p-3 md:col-span-2"
                  rows={3}
                />
              </div>
              <button onClick={addEvent} className="btn-primary mt-4">
                Add Event
              </button>
            </div>

            {/* Events List */}
            <div className="space-y-4">
              {events.map((event) => (
                <div key={event.id} className="glass-card p-4 flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-white">{event.title}</h4>
                    <p className="text-zinc-400 text-sm">{event.date} • {event.time}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleEventActive(event.id, event.is_active)}
                      className={`p-2 ${event.is_active ? "text-green-500" : "text-zinc-500"}`}
                    >
                      {event.is_active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                    </button>
                    <button onClick={() => deleteEvent(event.id)} className="p-2 text-red-500">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            {events.length === 0 && (
              <p className="text-zinc-500 text-center py-12">No events yet. Add one above!</p>
            )}
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <div>
            <h2 className="font-heading text-2xl text-white mb-6">Bookings</h2>
            <div className="space-y-4">
              {bookings.map((booking) => (
                <div key={booking.id} className="glass-card p-4">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h4 className="font-medium text-white">{booking.name}</h4>
                      <p className="text-zinc-400 text-sm">{booking.email} • {booking.phone}</p>
                      <p className="text-zinc-400 text-sm mt-1">
                        {booking.event_date} @ {booking.event_time} • {booking.guest_count} guests
                      </p>
                      <p className="text-rose text-sm mt-1">{booking.package_type}</p>
                      {booking.special_requests && (
                        <p className="text-zinc-500 text-sm mt-2">Note: {booking.special_requests}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={booking.status}
                        onChange={(e) => updateBookingStatus(booking.id, e.target.value)}
                        className={`bg-zinc-900 border text-sm p-2 ${
                          booking.status === "confirmed" ? "border-green-500 text-green-500" :
                          booking.status === "cancelled" ? "border-red-500 text-red-500" :
                          "border-yellow-500 text-yellow-500"
                        }`}
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {bookings.length === 0 && (
              <p className="text-zinc-500 text-center py-12">No bookings yet.</p>
            )}
          </div>
        )}

        {/* Inquiries Tab */}
        {activeTab === "inquiries" && (
          <div>
            <h2 className="font-heading text-2xl text-white mb-6">Inquiries</h2>
            <div className="space-y-4">
              {inquiries.map((inquiry) => (
                <div key={inquiry.id} className="glass-card p-4">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h4 className="font-medium text-white">{inquiry.name}</h4>
                      <p className="text-zinc-400 text-sm">{inquiry.email} {inquiry.phone && `• ${inquiry.phone}`}</p>
                      <p className="text-rose text-sm mt-1">{inquiry.service_type}</p>
                      <p className="text-zinc-300 mt-2">{inquiry.message}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={inquiry.status}
                        onChange={(e) => updateInquiryStatus(inquiry.id, e.target.value)}
                        className={`bg-zinc-900 border text-sm p-2 ${
                          inquiry.status === "closed" ? "border-green-500 text-green-500" :
                          inquiry.status === "contacted" ? "border-blue-500 text-blue-500" :
                          "border-yellow-500 text-yellow-500"
                        }`}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {inquiries.length === 0 && (
              <p className="text-zinc-500 text-center py-12">No inquiries yet.</p>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && settings && (
          <div>
            <h2 className="font-heading text-2xl text-white mb-6">Site Settings</h2>
            <div className="glass-card p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-zinc-400 text-sm mb-2 block">Patch Class Price ($)</label>
                  <input
                    type="number"
                    value={settings.patch_class_price}
                    onChange={(e) => setSettings({...settings, patch_class_price: parseInt(e.target.value)})}
                    className="w-full bg-zinc-900 border border-zinc-700 text-white p-3"
                  />
                </div>
                <div>
                  <label className="text-zinc-400 text-sm mb-2 block">Embellishment Class Price ($)</label>
                  <input
                    type="number"
                    value={settings.embellishment_class_price}
                    onChange={(e) => setSettings({...settings, embellishment_class_price: parseInt(e.target.value)})}
                    className="w-full bg-zinc-900 border border-zinc-700 text-white p-3"
                  />
                </div>
                <div>
                  <label className="text-zinc-400 text-sm mb-2 block">Max Guests</label>
                  <input
                    type="number"
                    value={settings.max_guests}
                    onChange={(e) => setSettings({...settings, max_guests: parseInt(e.target.value)})}
                    className="w-full bg-zinc-900 border border-zinc-700 text-white p-3"
                  />
                </div>
                <div>
                  <label className="text-zinc-400 text-sm mb-2 block">Contact Phone</label>
                  <input
                    type="text"
                    value={settings.contact_phone}
                    onChange={(e) => setSettings({...settings, contact_phone: e.target.value})}
                    className="w-full bg-zinc-900 border border-zinc-700 text-white p-3"
                  />
                </div>
                <div>
                  <label className="text-zinc-400 text-sm mb-2 block">Contact Email</label>
                  <input
                    type="email"
                    value={settings.contact_email}
                    onChange={(e) => setSettings({...settings, contact_email: e.target.value})}
                    className="w-full bg-zinc-900 border border-zinc-700 text-white p-3"
                  />
                </div>
                <div>
                  <label className="text-zinc-400 text-sm mb-2 block">Instagram URL</label>
                  <input
                    type="text"
                    value={settings.instagram_url}
                    onChange={(e) => setSettings({...settings, instagram_url: e.target.value})}
                    className="w-full bg-zinc-900 border border-zinc-700 text-white p-3"
                  />
                </div>
                <div>
                  <label className="text-zinc-400 text-sm mb-2 block">CashApp ID</label>
                  <input
                    type="text"
                    value={settings.cashapp_id}
                    onChange={(e) => setSettings({...settings, cashapp_id: e.target.value})}
                    className="w-full bg-zinc-900 border border-zinc-700 text-white p-3"
                  />
                </div>
                <div>
                  <label className="text-zinc-400 text-sm mb-2 block">Zelle Email</label>
                  <input
                    type="email"
                    value={settings.zelle_email}
                    onChange={(e) => setSettings({...settings, zelle_email: e.target.value})}
                    className="w-full bg-zinc-900 border border-zinc-700 text-white p-3"
                  />
                </div>
              </div>
              <button onClick={updateSettings} className="btn-gold mt-6">
                Save Settings
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
